import CustomLoadingButton from 'Components/CustomLoadingButton'
import { WithLoader } from 'Components/WithLoader'
import type {
  ParameterGroup,
  ParameterGroupParameter
} from 'Services/ProductManagement/Features/Parameter/types'
import ProductPricingActivePeriodDrawer from 'Services/ProductManagement/Features/Product/Components/ProductPricingActivePeriodDrawer'
import type { RowType } from 'Services/ProductManagement/Features/Product/Components/ProductPricingGrid/ProductPricingGrid'
import ProductPricingTabs from 'Services/ProductManagement/Features/Product/Components/ProductPricingTabs'
import {
  type PricingPlan,
  type Product,
  updateSingleProduct,
  type Validity
} from 'Services/ProductManagement/Features/Product/productSlice'
import type { ZoneDefinition } from 'Services/ProductManagement/Features/ZoneDefinition/types'
import { useGetZoneDefinitionsQuery } from 'Services/ProductManagement/Features/ZoneDefinition/zoneDefinitionApi'
import useTypedSelector, { useAppDispatch } from 'Store'
import getTranslation from 'Util/getTranslation'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import SaveIcon from '@mui/icons-material/Save'
import { TabContext, TabPanel } from '@mui/lab'
import { Box, Button, ButtonGroup, Divider, styled, Typography, useTheme } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface StyledButtonGroupButtonProps {
  selected?: boolean
}

const StyledButtonGroupButton = styled(Button)<StyledButtonGroupButtonProps>(
  ({ theme, selected }) => ({
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
    fontWeight: selected ? 700 : 600,
    color: theme.palette.text[selected ? 'primary' : 'secondary'],
    backgroundColor: selected ? theme.palette.action.selected : 'transparent'
  })
)

interface ProductPricingProps {
  product: Product
  domicileClassParameterGroup: ParameterGroup | null
  isEdited: boolean
  setIsEdited: (isEdited: boolean) => void
}

export const DOMICILE_CLASS_PARAMETER_KEY = 'domicileClass'

function ProductPricing({
  product,
  domicileClassParameterGroup,
  isEdited,
  setIsEdited
}: Readonly<ProductPricingProps>) {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const { confirm } = useConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: allZoneDefinitions = [], isLoading: isZoneDefinitionsLoading } =
    useGetZoneDefinitionsQuery(undefined)
  const zoneDefinition = allZoneDefinitions[0] ?? null
  const { submitLoading } = useTypedSelector(state => state.product)

  const domicileClasses = domicileClassParameterGroup?.parameters ?? []

  const tabFromParams = searchParams.get(DOMICILE_CLASS_PARAMETER_KEY) ?? ''
  const [isActivePeriodDrawerOpen, setIsActivePeriodDrawerOpen] = useState(false)
  const [productPricingPlans, setProductPricingPlans] = useState<PricingPlan[]>(
    product.pricing_plans
  )
  const [activePeriods, setActivePeriods] = useState<Array<Validity | null>>([])
  const [selectedActivePeriod, setSelectedActivePeriod] = useState<Validity | null>(null)
  const [editedPricingPlanCells, setEditedPricingPlanCells] = useState<RowType[]>([])
  const [updatedPricingPlanCells, setUpdatedPricingPlanCells] = useState<RowType[]>([])

  useEffect(() => {
    if (domicileClasses.length > 0 && !searchParams.get(DOMICILE_CLASS_PARAMETER_KEY)) {
      changeTab(domicileClasses[0].parameter_id)
    }
  }, [domicileClasses])

  const changeTab = (newValue: string) => {
    setIsEdited(false)
    searchParams.set(DOMICILE_CLASS_PARAMETER_KEY, newValue)
    setSearchParams(searchParams)
    setProductPricingPlans(product.pricing_plans)
    setActivePeriods(generateDistinctActivePeriods())
    setEditedPricingPlanCells([])
    setUpdatedPricingPlanCells([])
  }

  const handleDomicileClassChange = (newValue: string) => {
    if (isEdited) {
      confirm({
        title: t`Are you sure?`,
        message: t`Are you sure you want to continue? Unsaved changes will be lost.`,
        handleConfirm: () => {
          changeTab(newValue)
        }
      })
    } else {
      changeTab(newValue)
    }
  }

  const handleCloseActivePeriod = () => {
    setIsActivePeriodDrawerOpen(false)
    setSelectedActivePeriod(null)
  }

  const handleOpenActivePeriod = () => {
    setIsActivePeriodDrawerOpen(true)
  }

  const generateDistinctActivePeriods = (pricingPlans?: PricingPlan[]) => {
    // If product has no pricing plans generate default active period automatically
    if ([...(pricingPlans || productPricingPlans)].length < 1) return [null]

    const uniquePeriods = [...(pricingPlans || productPricingPlans)].reduce(
      (uniquePeriods, plan) => {
        // Get the title for grouping
        const title = plan.active_period?.title ?? ''

        // Check if we already have a period with this title in our results
        const periodExists = uniquePeriods.some(activePeriod =>
          activePeriod === null ? title === '' : activePeriod.title === title
        )

        // If not, add it to our results
        if (!periodExists) {
          uniquePeriods.push(plan.active_period ? { ...plan.active_period } : null)
        }
        return uniquePeriods
      },
      [] as Array<Validity | null>
    )

    // Order uniquePeriods so that the one with null active period is always first
    return uniquePeriods.sort((a, b) => {
      if (a === null) return -1
      if (b === null) return 1
      return a.title.localeCompare(b.title)
    })
  }

  /**
   * Handles the change in active periods.
   * Merges the current active periods with new ones generated from pricing plans,
   * ensuring no duplicates are added.
   */
  const handleActivePeriodsChange = () => {
    // Start with the current active periods to preserve them
    const currentActivePeriods = [...activePeriods]

    // Add any new active periods from pricing plans
    const newActivePeriods = generateDistinctActivePeriods()

    // Merge the lists, avoiding duplicates
    const mergedPeriods = [...currentActivePeriods]

    for (const newPeriod of newActivePeriods) {
      const periodExists = mergedPeriods.some(activePeriod =>
        activePeriod === null
          ? newPeriod === null
          : newPeriod !== null && activePeriod.title === newPeriod.title
      )

      if (!periodExists) {
        mergedPeriods.push(newPeriod)
      }
    }

    if (mergedPeriods.length === 0) {
      mergedPeriods.push(null)
    }
    setActivePeriods(mergedPeriods)
  }

  useEffect(() => {
    handleActivePeriodsChange()
  }, [productPricingPlans])

  const getPricingPlansByActivePeriod = (activePeriod: Validity | null, domicileClass: string) => {
    let pricingPlansByActivePeriod: PricingPlan[] = []

    if (activePeriod === null) {
      pricingPlansByActivePeriod = productPricingPlans.filter(plan => plan.active_period === null)
    } else {
      pricingPlansByActivePeriod = productPricingPlans.filter(plan => {
        return plan.active_period?.title === activePeriod?.title
      })
    }

    return pricingPlansByActivePeriod.filter(
      pricingPlan => pricingPlan.domicile_class === domicileClass
    )
  }

  /**
   * Returns false if the number of pricing plans exceeds the amount of zones in the zone definition.
   *
   * One active period represents one pricing grid, which can have one pricing plan per zone.
   * Therefore the amount of pricing plans per active period cannot exceed the amount of zones in the zone definition.
   * This is checked for each domicile class.
   */
  const validatePricingPlanAmount = (
    zoneDefinition: ZoneDefinition,
    activePeriods: (Validity | null)[],
    domicileClasses: ParameterGroupParameter[]
  ) => {
    const totalZonesAmount = zoneDefinition.zones.length + zoneDefinition.special_zones.length

    for (const domicileClass of domicileClasses) {
      for (const activePeriod of activePeriods) {
        const pricingPlansByActivePeriod = getPricingPlansByActivePeriod(
          activePeriod,
          domicileClass.parameter_id
        )
        if (pricingPlansByActivePeriod.length > totalZonesAmount) {
          return false
        }
      }
    }
    return true
  }

  const handleSave = (updatedPricingPlans: PricingPlan[]) => {
    if (!validatePricingPlanAmount(zoneDefinition, activePeriods, domicileClasses)) {
      SnackbarUtils.error(t`Pricing plan amount cannot exceed the number of available zones.`)
      return
    }

    dispatch(
      updateSingleProduct({
        id: product.id,
        pricing_plans: updatedPricingPlans
      })
    )
      .then(unwrapResult)
      .then((productUpdateResponse: Product) => {
        setUpdatedPricingPlanCells(
          productUpdateResponse.pricing_plans.length > 0 ? editedPricingPlanCells : []
        )
        setEditedPricingPlanCells([])
        setProductPricingPlans([...productUpdateResponse.pricing_plans])
        setIsEdited(false)
        SnackbarUtils.success(t`Successfully saved changes.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`An error occurred while saving changes.`)
      })
  }

  const filterPlansByActivePeriod = (plans: PricingPlan[], period: Validity) => {
    return plans.filter(plan => plan.active_period?.title !== period.title)
  }

  const filterPeriodsByTitle = (periods: Array<Validity | null>, title: string) => {
    return periods.filter(period => period?.title !== title)
  }

  const handleDeleteActivePeriod = (activePeriod: Validity | null) => () => {
    if (activePeriod === null) {
      return
    } else {
      setActivePeriods(prevPeriods => filterPeriodsByTitle(prevPeriods, activePeriod.title))
      const updatedPricingPlans = filterPlansByActivePeriod(productPricingPlans, activePeriod)
      handleSave(updatedPricingPlans)
    }
  }

  const handleSelectActivePeriod = (activePeriod: Validity | null) => {
    setSelectedActivePeriod(activePeriod)
    setIsActivePeriodDrawerOpen(true)
  }

  const updateActivePeriod = (oldActivePeriod: Validity | null, newActivePeriod: Validity) => {
    setProductPricingPlans(prevPlans =>
      prevPlans.map(plan => {
        if (plan.active_period?.title === oldActivePeriod?.title) {
          return {
            ...plan,
            active_period: newActivePeriod
          }
        }
        return plan
      })
    )
    setActivePeriods(prevPeriods =>
      prevPeriods.map(period => {
        if (period?.title === oldActivePeriod?.title) {
          return newActivePeriod
        }
        return period
      })
    )
  }

  const isSubmitLoading = submitLoading === 'pending'

  return (
    <TabContext value={tabFromParams}>
      <Box
        sx={{
          backgroundColor: 'background.default',
          p: 2
        }}
      >
        <ButtonGroup
          variant="text"
          sx={{
            '.MuiButtonGroup-grouped:not(:last-of-type)': {
              borderColor: theme.palette.action.selected
            }
          }}
          aria-label={t`Domicile class selection buttons`}
        >
          {domicileClasses.map(domicileClass => (
            <StyledButtonGroupButton
              key={domicileClass.parameter_id}
              size="small"
              variant="text"
              onClick={() => handleDomicileClassChange(domicileClass.parameter_id)}
              selected={domicileClass.parameter_id === tabFromParams}
            >
              {getTranslation(domicileClass.translations, 'fi').value}
            </StyledButtonGroupButton>
          ))}
        </ButtonGroup>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        {domicileClasses.map(domicileClass => (
          <TabPanel
            sx={{ p: 0 }}
            key={domicileClass.parameter_id}
            value={domicileClass.parameter_id}
          >
            <WithLoader loading={isZoneDefinitionsLoading}>
              {zoneDefinition ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t`Pricing by zone`}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {getTranslation(product.translations, 'fi').name}
                    </Typography>
                  </Box>
                  {activePeriods.map(activePeriod => {
                    return (
                      <ProductPricingTabs
                        key={`zoneDefinition-${activePeriod?.title ?? 'default'}`}
                        pricingPlans={getPricingPlansByActivePeriod(
                          activePeriod,
                          domicileClass.parameter_id
                        )}
                        zoneDefinition={zoneDefinition}
                        domicileClass={domicileClass}
                        product={product}
                        isEdited={isEdited}
                        setIsEdited={setIsEdited}
                        handleOpenActivePeriod={handleOpenActivePeriod}
                        pricingPlanActivePeriod={activePeriod}
                        setProductPricingPlans={setProductPricingPlans}
                        handleSelectActivePeriod={handleSelectActivePeriod}
                        handleDeleteActivePeriod={handleDeleteActivePeriod(activePeriod)}
                        editedPricingPlanCells={editedPricingPlanCells}
                        setEditedPricingPlanCells={setEditedPricingPlanCells}
                        updatedPricingPlanCells={updatedPricingPlanCells}
                        setUpdatedPricingPlanCells={setUpdatedPricingPlanCells}
                      />
                    )
                  })}
                  <Box display="flex" gap={2} mt={2}>
                    <Button
                      onClick={() =>
                        confirm({
                          title: t`Are you sure?`,
                          message: t`All changes will be lost. Are you sure you want to cancel?`,
                          handleConfirm: () => {
                            setProductPricingPlans(product.pricing_plans)
                            setActivePeriods(generateDistinctActivePeriods(product.pricing_plans))
                            setIsEdited(false)
                            setEditedPricingPlanCells([])
                          }
                        })
                      }
                      disabled={!isEdited}
                      variant="outlined"
                      color="inherit"
                    >
                      {t`Cancel`}
                    </Button>
                    <CustomLoadingButton
                      size="medium"
                      disabled={!isEdited}
                      onClick={() => handleSave(productPricingPlans)}
                      isLoading={isSubmitLoading}
                      icon={<SaveIcon />}
                      iconPosition="start"
                    >
                      {t`Save changes`}
                    </CustomLoadingButton>
                  </Box>
                </>
              ) : (
                <Typography variant="subtitle2">
                  {t`Product pricing is not available when PTA does not have zone definition defined.`}
                </Typography>
              )}
            </WithLoader>
          </TabPanel>
        ))}
        <ProductPricingActivePeriodDrawer
          open={isActivePeriodDrawerOpen}
          selectedActivePeriod={selectedActivePeriod}
          handleClose={handleCloseActivePeriod}
          activePeriods={activePeriods}
          setActivePeriods={setActivePeriods}
          updateActivePeriod={updateActivePeriod}
        />
      </Box>
    </TabContext>
  )
}

export default ProductPricing
